import { type ReactNode } from "react";
import { Navigate } from "react-router-dom";

import { rolesOf, useCaller, useTrackerRoles } from "@/lib/permissions";

export interface RequirePlatformAdminProps {
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Mounts its children only for a caller the API calls a platform-admin — our
 * staff's platform-wide screens (WorkspaceService.GetCaller, lib/permissions).
 */
export function RequirePlatformAdmin({ fallback, children }: RequirePlatformAdminProps) {
  const { isPlatformAdmin } = useTrackerRoles();
  if (!isPlatformAdmin) {
    return <>{fallback ?? <Navigate to="/" replace />}</>;
  }
  return <>{children}</>;
}

/**
 * Mounts its children only for an admin of the caller's org (or a
 * platform-admin). Waits for GetCaller rather than bouncing a deep link while
 * the roles are still on their way.
 */
export function RequireTrackerAdmin({ fallback, children }: RequirePlatformAdminProps) {
  const caller = useCaller();
  const { isTrackerAdmin, isPlatformAdmin } = rolesOf(caller.data);
  if (caller.isPending) return null;
  if (!isTrackerAdmin && !isPlatformAdmin) {
    return <>{fallback ?? <Navigate to="/" replace />}</>;
  }
  return <>{children}</>;
}
